import { z } from 'zod'

import { pruned } from '@/lib/filters'
import { columnFiltersSchema } from '@/lib/tableFilters'

/**
 * The URL state of Focused SKUs Analysis.
 *
 * Everything that changes what the sheet shows is here, so a view can be sent
 * to a colleague: the scope (warehouse, store or all stores, brands), the
 * grain and period, the search, the column filters, the sort and the page;
 * or, in the history view, the SKU it follows.
 * Unsaved notes are not — they are work in progress, not view state.
 *
 * Every field is optional with `.catch`, and defaults are applied at read
 * time and pruned on the way out, so `/focused-data/analyzer` stays clean
 * until something differs from the default.
 */

export const PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 50
export const DEFAULT_SORT = 'retailer_sku'

/** The store picker's "All stores" choice: the warehouse's combined sheet. */
export const ALL_STORES = 'all'

const id = z.number().int().positive().optional().catch(undefined)

export const analysisSearchSchema = z.object({
  warehouse: id.describe(
    'The warehouse whose focused SKUs the sheet reads, by id. Changing it clears the store and brands.',
  ),
  store: z
    .union([z.literal(ALL_STORES), z.number().int().positive()])
    .optional()
    .catch(undefined)
    .describe(
      "The store, by id, or all for the warehouse's combined sheet. Nothing loads until a warehouse and a store are chosen.",
    ),
  brands: z
    .array(z.number().int().positive())
    .optional()
    .catch(undefined)
    .describe('Brands to narrow the sheet to, by id, applied with Go. Empty means every brand.'),
  grain: z
    .enum(['weekly', 'monthly'])
    .optional()
    .catch(undefined)
    .describe(
      'Weekly or monthly goal tracker: T is the period, T-1 the one before. Default weekly.',
    ),
  /** The period's first day: a Sunday, or a 1st. Absent, the latest finished one. */
  period: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined)
    .describe(
      "The period's first day, YYYY-MM-DD: a Sunday for a week, a 1st for a month. Default the latest finished period.",
    ),
  q: z
    .string()
    .max(200)
    .optional()
    .catch(undefined)
    .describe('Search over the listing identifier, SKU code and title, in the browser.'),
  sort: z
    .string()
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The column the sheet is sorted by; none means unsorted. Default retailer_sku.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction. Default asc.'),
  /** The header funnels: one filter per column. */
  filters: columnFiltersSchema
    .optional()
    .catch(undefined)
    .describe('The header funnels: one {id, value} filter per column, applied in the browser.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the sheet. Default 1.'),
  size: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page. Default 50.'),
  /** `history`: one SKU across every period (VentaHub's Snapshot History). Absent, the sheet. */
  view: z
    .enum(['sheet', 'history'])
    .optional()
    .catch(undefined)
    .describe(
      'sheet: every focused SKU for one period; history: one SKU across every period. Default sheet.',
    ),
  /** The SKU the history follows, by id. */
  sku: id.describe('The SKU the history view follows, by internal SKU id.'),
})

export type AnalysisSearch = z.infer<typeof analysisSearchSchema>

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutAnalysisDefaults(search: AnalysisSearch): AnalysisSearch {
  return pruned({
    ...search,
    grain: search.grain === 'weekly' ? undefined : search.grain,
    sort: search.sort === DEFAULT_SORT ? undefined : search.sort,
    order: search.order === 'asc' ? undefined : search.order,
    size: search.size === DEFAULT_PAGE_SIZE ? undefined : search.size,
    filters: search.filters?.length ? search.filters : undefined,
    view: search.view === 'sheet' ? undefined : search.view,
  })
}
